import { useCallback } from 'react'
import { sequenceLinks, unsequenceLinks, sequenceCreated } from '@trackmind/core'
import type { Task } from '@/types'
import { useApp } from '@/context/AppContext'
import { trackFeatureUse } from '@/lib/analytics'

/**
 * Writing a chain of dependencies in one go.
 *
 * Both entry points — "Link as a sequence" on a project, and the Sequence
 * toggle on bulk capture — end up here, so the rules about loops and about
 * not trampling hand-made dependencies are applied once.
 */
export function useSequence() {
  const { tasks, saveTask } = useApp()

  const persist = useCallback(async (
    changes: { id: string; dependencies: string[] }[],
  ) => {
    const now = new Date().toISOString()
    for (const change of changes) {
      const target = tasks.find(t => t.id === change.id)
      if (!target) continue
      await saveTask({ ...target, dependencies: change.dependencies, updated_at: now })
    }
  }, [tasks, saveTask])

  /** Chain a list in the order given. Returns what it did. */
  const linkSequence = useCallback(async (ordered: Task[]) => {
    const result = sequenceLinks(ordered, tasks)
    await persist(result.changes)
    if (result.changes.length > 0) trackFeatureUse('sequence_link')
    return result
  }, [tasks, persist])

  /** Remove exactly the links that chaining this order would have made. */
  const unlinkSequence = useCallback(async (ordered: Task[]) => {
    const result = unsequenceLinks(ordered, tasks)
    await persist(result.changes)
    if (result.changes.length > 0) trackFeatureUse('sequence_unlink')
    return result
  }, [tasks, persist])

  /**
   * Chain tasks that have only just been created.
   *
   * `created` is passed in rather than read from context because the context
   * copy may not have caught up with the writes yet — the ids and the task
   * objects both come from the creating call.
   */
  const linkCreated = useCallback(async (createdIds: string[], created: Task[]) => {
    const pool = [...tasks.filter(t => !createdIds.includes(t.id)), ...created]
    const result = sequenceCreated(createdIds, pool)
    const now = new Date().toISOString()
    for (const change of result.changes) {
      const target = pool.find(t => t.id === change.id)
      if (!target) continue
      await saveTask({ ...target, dependencies: change.dependencies, updated_at: now })
    }
    if (result.changes.length > 0) trackFeatureUse('sequence_capture')
    return result
  }, [tasks, saveTask])

  return { linkSequence, unlinkSequence, linkCreated }
}
